import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { SEND_FROM_ADDRESSES, SIMS_ADDRESSES, SIMULATION_CATEGORIES, ENVIRONMENT_NAME } from "../../config";
import { useGetTenanciesQuery } from "../../store/services/tenancy.service";
import { useLazyGetTinyJwtTokenQuery } from "../../store/services/simulation-service";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Switch,
    Tooltip,
    Typography,
    TextField,
    Modal,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider
} from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import MainCard from "../../components/cards/MainCard";
import InfoIcon from "@mui/icons-material/Info";
import HelpIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useGetCoursesQuery } from "../../store/services/university-service";
import { useTheme } from "@mui/material/styles";
import AllLandingPages from "../../themes/PresetLandingPages";
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import { getCardColor } from "../../utils/colorUtils";
import TrimmedTextField from "../../components/TrimmedTextField";

SimulationForm.propTypes = {
    initialData: PropTypes.object,
    onSave: PropTypes.func,
    mode: PropTypes.string
};

export default function SimulationForm(props) {
    const { initialData, onSave, mode } = props;

    const theme = useTheme();
    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user.role === "Administrators";
    const lightMode = theme.palette.mode === "light";

    // Use tenancyId from Redux instead of user.tenancyId so sims are created against the toggled tenancy
    const { tenancyId } = useSelector((state) => state.tenancy);

    const [simulationData, setSimulationData] = useState({
        ...initialData,
        published: initialData.published !== undefined ? initialData.published : true,
        // Fall back to the login tenancy if the toggle is unset; Number("") is 0, which means "shared globally"
        sharedTenancyIds: initialData.sharedTenancyIds || (isAdmin ? [] : [Number(tenancyId) || user.tenancyId]),
        publishToFamily: initialData.publishToFamily || false
    });

    const [coursesOptions, setCoursesOptions] = useState([
        { id: "Phishing 101", name: "Phishing 101" }
    ]);
    const [loading, setLoading] = useState(false);

    // Modal and copy functionality states
    const [placeholderModalOpen, setPlaceholderModalOpen] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});

    const [triggerJwtToken, { data: jwtToken, isLoading: jwtTokenLoading, error: jwtTokenError }] = useLazyGetTinyJwtTokenQuery();

    const sendFromAddressOptions = SEND_FROM_ADDRESSES.split(",");
    const simulationTypeOptions = SIMULATION_CATEGORIES.split(",");
    const simulationDomainNameOptions = SIMS_ADDRESSES.split(",");
    const simulationDifficultyRatingOptions = ["0", "1", "2", "3"];
    const [templatePath, setTemplatePath] = useState(initialData.landingPagePath || "");

    const [content, setContent] = useState(simulationData.content);

    const { data: tenanciesData, isLoading: tenanciesDataLoading } = useGetTenanciesQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    const { data: coursesData, isLoading: coursesLoading } = useGetCoursesQuery({
        perPage: 100,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    useEffect(() => {
        if (coursesData) {
            setCoursesOptions(prevOptions => [...prevOptions, ...coursesData]);
        }
    }, [coursesData]);

    // Initialize component
    useEffect(() => {
        // Component initialization
    }, []);

    const handleSharedTenanciesChange = (tenancyIds) => {
        setSimulationData(prevData => ({
            ...prevData,
            sharedTenancyIds: tenancyIds
        }));
    };

    const handlePublishToFamilyChange = (event) => {
        setSimulationData(prevData => ({
            ...prevData,
            publishToFamily: event.target.checked
        }));
    };

    const handleSave = async () => {
        // Validate simulation name
        if (!simulationData.simulationName || simulationData.simulationName.trim() === "") {
            return;
        }

        setLoading(true);
        try {
            // Use simulationData.landingPagePath directly, falling back to templatePath for backwards compatibility
            const landingPagePathToSave = simulationData.landingPagePath || templatePath;
            const saveData = { ...simulationData, landingPagePath: landingPagePathToSave };
            console.log("Saving with landingPagePath:", landingPagePathToSave);
            await onSave(saveData);
        } catch (error) {
            console.error("Error saving simulation:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadTemplatePath = useCallback(
        (landingPage) => {
            if (landingPage) {
                setTemplatePath(landingPage.landingPagePath);
                // Update the simulation data with the landing page path
                setSimulationData({
                    ...simulationData,
                    landingPagePath: landingPage.landingPagePath
                });
            }
        },
        [simulationData, setSimulationData]
    );

    const selectedLandingPage = useMemo(() => {
        if (!simulationData.landingPagePath) return null;

        // Backup for cnde environment
        if (ENVIRONMENT_NAME === 'cnde' && simulationData.landingPagePath.includes('Microsoft')) {
            return AllLandingPages[0]; // Microsoft Login
        }

        if (ENVIRONMENT_NAME === 'cnde' && simulationData.landingPagePath.includes('Google')) {
            return AllLandingPages[1]; // Google Login
        }

        // Special case for default landing page
        if (simulationData.landingPagePath === "/landing-page/default") {
            return AllLandingPages.find(page =>
                page.landingPagePath && page.landingPagePath === "/landing-page/default");
        }

        // Try exact match first
        const exactMatch = AllLandingPages.find(page =>
            page.landingPagePath && page.landingPagePath === simulationData.landingPagePath);
        if (exactMatch) return exactMatch;

        const savedPath = simulationData.landingPagePath.toLowerCase();

        // Try matching by name keywords
        if (savedPath.includes('microsoft')) {
            const match = AllLandingPages.find(page =>
                page.name && page.name.toLowerCase().includes('microsoft'));
            if (match) return match;
        }

        if (savedPath.includes('google')) {
            const match = AllLandingPages.find(page =>
                page.name && page.name.toLowerCase().includes('google'));
            if (match) return match;
        }

        // Try path substring matching
        for (const page of AllLandingPages) {
            if (!page.landingPagePath) continue;
            const optionPath = page.landingPagePath.toLowerCase();
            // Check if one path is contained within the other
            if ((savedPath.includes(optionPath) || optionPath.includes(savedPath))
                && optionPath !== "/landing-page/default") {
                return page;
            }
        }

        // Try folder matching for landingpage URLs
        if (savedPath.includes('landingpage/')) {
            for (const page of AllLandingPages) {
                if (!page.landingPagePath || page.landingPagePath === "/landing-page/default") continue;

                const optionPath = page.landingPagePath.toLowerCase();
                let pathMatch = false;

                if (optionPath.includes('landingpage/')) {
                    const getFolder = (path) => {
                        const match = path.match(/landingpage\/([^\/]+)/);
                        return match ? decodeURIComponent(match[1].toLowerCase()) : '';
                    };

                    const savedFolder = getFolder(savedPath);
                    const optionFolder = getFolder(optionPath);

                    if (savedFolder && optionFolder &&
                        (savedFolder === optionFolder ||
                            savedFolder.includes(optionFolder) ||
                            optionFolder.includes(savedFolder))) {
                        pathMatch = true;
                    }
                }

                if (pathMatch) return page;
            }
        }

        // Try domain matching for full URLs
        if (savedPath.includes('://')) {
            for (const page of AllLandingPages) {
                if (!page.landingPagePath) continue;
                const optionPath = page.landingPagePath.toLowerCase();
                if (optionPath.includes('://')) {
                    try {
                        // Extract domains to compare
                        const savedDomain = new URL(savedPath).hostname;
                        const optionDomain = new URL(optionPath).hostname;

                        if (savedDomain === optionDomain) {
                            return page;
                        }
                    } catch (e) {
                        // URL parsing error, continue to next option
                    }
                }
            }
        }

        return null;
    }, [simulationData.landingPagePath]);

    const placeholders = [
        { placeholder: "{user.action}", description: "Button that the user clicks on" },
        { placeholder: "{user.email}", description: "Recipient's email address" },
        { placeholder: "{user.firstname}", description: "Recipient's first name" },
        { placeholder: "{user.lastname}", description: "Recipient's last name" },
        { placeholder: "{tenancy.name}", description: "Tenancy Name" }
    ];

    // Copy to clipboard functionality
    const copyToClipboard = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, 1500);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, []);

    const editorStyle = `
        body { 
            background-color: ${lightMode ? 'white' : theme.palette.black.main}; 
            color: ${lightMode ? 'black' : theme.palette.grey[50]}; 
        }
    `;

    const tinydriveTokenProvider = useCallback((callback) => {
        const getToken = async () => {
            try {
                const response = await triggerJwtToken().unwrap();
                if (response) {
                    callback(response);
                } else {
                    console.warn("JWT token not available in the response");
                    callback(null);
                }
            } catch (error) {
                console.error("Error fetching JWT token:", error);
                callback(null);
            }
        };

        getToken();
    }, [triggerJwtToken]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
        .tox-tinymce {
            border-color: ${lightMode ? theme.palette.grey[200] : theme.palette.black.light} !important;
        }
        .tox .tox-statusbar {
            background-color: ${lightMode ? 'inherit' : theme.palette.black.light} !important;
            color: ${lightMode ? 'inherit' : theme.palette.grey[300]} !important;
        }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [theme, lightMode]);

    // Check if simulation name is valid
    const isSimulationNameValid = simulationData.simulationName && simulationData.simulationName.trim() !== "";

    return (
        <>
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={6}>
                    <TrimmedTextField
                        label="Simulation Name"
                        required={true}
                        value={simulationData.simulationName}
                        size="medium"
                        fullWidth
                        onChange={(e) => {
                            setSimulationData({ ...simulationData, simulationName: e.target.value });
                        }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Email Subject"
                        value={simulationData.emailSubject}
                        size="medium"
                        fullWidth
                        onChange={(e) => {
                            setSimulationData({ ...simulationData, emailSubject: e.target.value });
                        }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Email Display Name"
                        value={simulationData.emailDisplayName}
                        size="medium"
                        fullWidth
                        onChange={(e) => {
                            setSimulationData({ ...simulationData, emailDisplayName: e.target.value });
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        id="email-from-address"
                        size="medium"
                        fullWidth
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        value={simulationData.emailFromAddress}
                        options={sendFromAddressOptions}
                        sx={{ mb: 2 }}
                        onChange={(e, value) => {
                            setSimulationData({ ...simulationData, emailFromAddress: value });
                        }}
                        renderInput={(params) => <TextField {...params} label="Email From Address" />}
                    />
                    <Autocomplete
                        id="simulation-type"
                        size="medium"
                        fullWidth
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        value={simulationData.simulationType}
                        disabled={!isAdmin}
                        options={simulationTypeOptions}
                        sx={{ mb: 2 }}
                        onChange={(e, value) => {
                            setSimulationData({ ...simulationData, simulationType: value });
                        }}
                        renderInput={(params) => <TrimmedTextField {...params} label="Simulation Type" />}
                    />
                    <Autocomplete
                        id="simulation-domain-name"
                        size="medium"
                        fullWidth
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        value={simulationData.simsDomainName}
                        options={simulationDomainNameOptions}
                        //disabled={!!templatePath} // Disable if second stage is selected
                        sx={{ mb: 2 }}
                        onChange={(e, value) => {
                            // Clear landingPagePath if a simulation domain is selected
                            if (value) {
                                setTemplatePath("");
                                setSimulationData({
                                    ...simulationData,
                                    simsDomainName: value,
                                    landingPagePath: ""
                                });
                            } else {
                                setSimulationData({ ...simulationData, simsDomainName: value });
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Simulation Domain Name" />}
                    />
                    <Autocomplete
                        options={AllLandingPages}
                        getOptionLabel={(option) => option.name}
                        value={selectedLandingPage}
                        //disabled={!!simulationData.simsDomainName} // Disable if domain name is selected
                        renderInput={(params) => (
                            <TrimmedTextField
                                {...params}
                                label="Select Second Stage"
                                fullWidth
                            />
                        )}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                setTemplatePath(newValue.landingPagePath);
                                setSimulationData({
                                    ...simulationData,
                                    simsDomainName: "",
                                    landingPagePath: newValue.landingPagePath
                                });
                            } else if (selectedLandingPage !== null) {
                                // Only clear if user explicitly deselected a matched template
                                setTemplatePath("");
                                setSimulationData({
                                    ...simulationData,
                                    landingPagePath: ""
                                });
                            }
                            // If selectedLandingPage was already null (custom path), don't clear
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        id="simulation-difficulty-rating"
                        size="medium"
                        disableClearable
                        fullWidth
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        value={simulationData.difficultyRating}
                        options={simulationDifficultyRatingOptions}
                        sx={{ mb: 3 }}
                        onChange={(e, value) => {
                            setSimulationData({ ...simulationData, difficultyRating: value });
                        }}
                        renderInput={(params) => <TrimmedTextField {...params} label="Simulation Difficulty Rating" />}
                    />
                    <div style={{ display: "flex", alignItems: "flex-start", mt: 2, }}>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="warning"
                            sx={{ whitespace: "nowrap", mr: 2 }}
                            disabled={!isSimulationNameValid || loading}
                            endIcon={loading ? (
                                <CircularProgress
                                    size={20}
                                    sx={{
                                        color: (theme) => theme.palette.getContrastText(theme.palette.warning.main)
                                    }}
                                />
                            ) : null}
                        >
                            {loading
                                ? (mode === "view" ? "Updating..." : "Creating...")
                                : (mode === "view" ? "Update Simulation" : "Create Simulation")
                            }
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{ whitespace: "nowrap" }}
                            endIcon={<InfoIcon />}
                            onClick={() => setPlaceholderModalOpen(true)}
                        >
                            View Placeholders
                        </Button>
                    </div>

                </Grid>

                {isAdmin && <Grid item xs={12} sm={6}>
                    {tenanciesDataLoading ?
                        <MainCard content={true} boxShadow={true} border={false}
                            sx={{ bgcolor: "background.default", alignContent: "center", mb: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                                <CircularProgress size={32} sx={{ mr: 2 }} />
                                <Typography variant="h6">
                                    Loading Send To Tenancies Setting
                                </Typography>
                            </Box>
                        </MainCard>
                        :
                        <>
                            {/* Visibility Card */}
                            <MainCard
                                content={true}
                                boxShadow={true}
                                border={false}
                                sx={{
                                    mb: 3,
                                    bgcolor: getCardColor(lightMode, simulationData.published)
                                }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="h4">Simulation Visibility</Typography>
                                        <Tooltip title="When enabled, the simulation will be visible to users according to sharing settings" placement="top">
                                            <IconButton size="small">
                                                <HelpIcon sx={{ color: "secondary.main" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Switch
                                        checked={simulationData.published}
                                        onChange={(e) => setSimulationData({ ...simulationData, published: e.target.checked })}
                                        inputProps={{ "aria-label": "simulation visibility" }}
                                        disabled={mode === "add-on-demand"}
                                    />
                                </Box>
                                <Typography variant="caption">
                                    {simulationData.published
                                        ? "The simulation is visible to users according to sharing settings"
                                        : "The simulation is currently hidden from all users"}
                                </Typography>
                            </MainCard>

                            {/* Simulation Sharing Card */}
                            <MainCard
                                content={true}
                                boxShadow={true}
                                border={false}
                                sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                            >
                                <Typography variant="h4" sx={{ mb: 2 }}>Simulation Sharing Settings</Typography>

                                {/* Tenancy Selection Component */}

                                <TenancySelectionComponent
                                    selectedTenancies={simulationData.sharedTenancyIds || []}
                                    onChange={handleSharedTenanciesChange}
                                    disabled={mode === "view" && !isAdmin}
                                />

                                {/* Publish to Family Switch */}
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 1,
                                    mb: 1,
                                    bgcolor: getCardColor(lightMode),
                                    borderRadius: 1
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="body1">Share with Tenancy Family</Typography>
                                        <Tooltip title="When enabled, this simulation will be accessible to all descendants of the selected tenancies" placement="top">
                                            <IconButton size="small">
                                                <HelpIcon sx={{ color: "secondary.main" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Switch
                                        checked={simulationData.publishToFamily || false}
                                        onChange={handlePublishToFamilyChange}
                                        inputProps={{ "aria-label": "publish to family" }}
                                        disabled={simulationData.sharedTenancyIds?.includes(0)}
                                    />
                                </Box>

                                {simulationData.sharedTenancyIds?.includes(0) && (
                                    <Typography variant="caption">
                                        "Family sharing" is disabled because the simulation is shared with all tenancies.
                                    </Typography>
                                )}
                            </MainCard>
                        </>
                    }

                </Grid>}

                <Grid item xs={12} sm={6}>
                    {/* Linked University Course Card */}
                    {coursesLoading ?
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default", alignContent: "center", mb: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                                <CircularProgress size={32} sx={{ mr: 2 }} />
                                <Typography variant="h6">
                                    Loading Course Linking Setting
                                </Typography>
                            </Box>
                        </MainCard>
                        :
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: getCardColor(lightMode, simulationData.linkedUniversityCourseId !== null && simulationData.linkedUniversityCourseId !== "") }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">
                                    Auto-assign Course
                                </Typography>

                                <Switch
                                    checked={simulationData.linkedUniversityCourseId !== null && simulationData.linkedUniversityCourseId !== ""}
                                    onChange={(e) => setSimulationData({
                                        ...simulationData,
                                        linkedUniversityCourseId: e.target.checked ? "Phishing 101" : null
                                    })}
                                    inputProps={{ "aria-label": "controlled" }}
                                />
                            </Box>
                            {simulationData.linkedUniversityCourseId !== null && simulationData.linkedUniversityCourseId !== "" &&
                                <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%", mt: 2 }}>
                                    <Autocomplete
                                        id="tenancy-id"
                                        size="small"
                                        selectOnFocus
                                        clearOnBlur
                                        handleHomeEndKeys
                                        options={coursesOptions ? coursesOptions : []}
                                        isOptionEqualToValue={(option, value) => option.id === value.linkedUniversityCourseId}
                                        value={coursesOptions && coursesOptions.find(row => row.id === simulationData.linkedUniversityCourseId) || null}
                                        getOptionLabel={(option) => option.id}
                                        sx={{ mt: 2, width: "100%" }}
                                        onChange={(e, value) => setSimulationData({
                                            ...simulationData,
                                            linkedUniversityCourseId: value ? value.id : ""
                                        })}
                                        renderInput={(params) => (<TrimmedTextField {...params} label="" />)}
                                    />
                                </Box>
                            }
                        </MainCard>
                    }
                </Grid>

                {!isAdmin && <Grid item xs={12} sm={6}>
                    {/* Simulation Sharing Card for Non-Admin Users */}
                    <MainCard
                        content={true}
                        boxShadow={true}
                        border={false}
                        sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                    >
                        <Typography variant="h4">Simulation Sharing Settings</Typography>

                        {/* Publish to Family Switch */}
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            bgcolor: getCardColor(lightMode),
                            borderRadius: 1
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">Share with Tenancy Family</Typography>
                                <Tooltip title="When enabled, this simulation will be accessible to all descendants of the selected tenancies" placement="top">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Switch
                                checked={simulationData.publishToFamily || false}
                                onChange={handlePublishToFamilyChange}
                                inputProps={{ "aria-label": "publish to family" }}
                                disabled={simulationData.sharedTenancyIds?.includes(0)}
                            />
                        </Box>
                    </MainCard>
                </Grid>}
            </Grid>

            <Box sx={{ minHeight: '850px', height: '850px' }}>
                <Editor
                    apiKey="j2g2hy77ifpdh2oe7hfarnk6vvls82qlv49guynyzw6d22ha"
                    value={simulationData.content}
                    onEditorChange={(newContent) => setSimulationData({ ...simulationData, content: newContent })}
                    init={{
                        skin: lightMode ? 'oxide' : 'oxide-dark',
                        content_css: lightMode ? 'default' : 'dark',
                        content_style: editorStyle,
                        plugins: [
                            "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
                            "searchreplace", "visualblocks", "code", "fullscreen",
                            "insertdatetime", "media", "table", "code", "help", "wordcount", "template", "tinydrive"
                        ],
                        images_file_types: "jpg,png,jpeg,webp",
                        tinydrive_token_provider: tinydriveTokenProvider,
                        toolbar:
                            "template image | code | undo redo | fontselect fontsize fontsizeselect bold italic forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link",
                        templates: [
                            {
                                title: "User name",
                                description: "Insert the users full name",
                                content: "{user.name}"
                            }
                        ],
                        file_picker_types: 'media image',
                        height: 850,
                        min_height: 850,
                        promotion: false,
                    }}
                />
            </Box>

            {/* Placeholder Modal */}
            <Modal
                open={placeholderModalOpen}
                onClose={() => setPlaceholderModalOpen(false)}
                aria-labelledby="placeholder-modal-title"
                aria-describedby="placeholder-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 600,
                        height: "80%",
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: theme.borderRadius.sm,
                        outline: 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Fixed Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 3,
                            pb: 2,
                            flexShrink: 0
                        }}
                    >
                        <Typography id="placeholder-modal-title" variant="h4">
                            Available Placeholders
                        </Typography>
                        <IconButton
                            onClick={() => setPlaceholderModalOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ flexShrink: 0 }} />

                    {/* Scrollable Content Container */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 3,
                            pt: 2
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Click the copy icon to copy a placeholder to your clipboard, then paste it into the editor.
                        </Typography>
                        <List sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                            borderRadius: theme.borderRadius.sm,
                            p: 1
                        }}>
                            {placeholders.map((item, index) => (
                                <React.Fragment key={item.placeholder}>
                                    <ListItem
                                        sx={{
                                            py: 2,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                                borderRadius: 1
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.95rem',
                                                        fontWeight: 600,
                                                        color: theme.palette.primary.main
                                                    }}
                                                >
                                                    {item.placeholder}
                                                </Typography>
                                            }
                                            secondary={item.description}
                                            secondaryTypographyProps={{
                                                sx: { mt: 0.5, fontSize: '0.875rem' }
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() => copyToClipboard(item.placeholder, item.placeholder)}
                                                size="small"
                                                sx={{ ml: 2 }}
                                            >
                                                {copiedStates[item.placeholder] ?
                                                    <CheckIcon fontSize="small" color="success" /> :
                                                    <ContentCopyIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < placeholders.length - 1 && <Divider variant="middle" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}